import { Info } from 'lucide-react'

interface InfoTipProps {
  text: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  /** If provided, the trigger renders the children instead of the default ⓘ icon. */
  children?: React.ReactNode
}

/**
 * A small "more info on hover" tooltip. Default trigger is a subtle (i) icon
 * that pairs naturally with form fields, headings, or status pills. Pass
 * `children` to wrap any element with the same hover hint.
 *
 * Pure Tailwind, no library. Uses group-hover + transitions, accessible via
 * the role/aria attributes on the tooltip itself.
 */
export default function InfoTip({ text, side = 'top', className = '', children }: InfoTipProps) {
  const sideClasses: Record<NonNullable<InfoTipProps['side']>, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  }

  return (
    <span className={`relative inline-flex items-center group ${className}`}>
      {children ?? (
        <Info
          className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors"
          aria-hidden="true"
        />
      )}
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-50 whitespace-pre-line rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal text-white shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 max-w-xs ${sideClasses[side]}`}
      >
        {text}
      </span>
    </span>
  )
}
